import React, { useMemo, useState } from 'react';
import { Badge } from '../ui/badge';
import { CheckCircle2, XCircle, Slash, AlertTriangle, MessageSquare, Filter, Bell } from 'lucide-react';
import { fmtETDateTime, ET_TIMEZONE } from '../../utils/tz';

/**
 * Per-customer Communication History — sent + skipped messages in chronological order.
 * Filters by Sent / Skipped / All. Highlights the reason on skipped rows.
 *
 * Backed by GET /api/admin/customers/{id}/sms-history which now returns skip_reason
 * + appointment_id + error fields. Read-only by design.
 */
const EVENT_LABEL = {
  appointment_confirmation: 'Appointment Confirmation',
  appointment_reschedule:   'Schedule Update',
  tech_en_route:            'ETA Notification',
  portal_invite:            'Portal Invite',
  google_review_request:    'Review Request',
  membership_renewal:       'Membership Renewal',
  payment_reminder:         'Payment Reminder',
  verification_code:        'Verification Code',
  manual_test:              'Test Send',
  schedule_change:          'Schedule Change',
  job_assignment:           'Job Assignment',
  dispatch_update:          'Dispatch Update',
};

const SKIP_REASON_LABEL = {
  publish_silent:        'Dispatcher published silently',
  dispatcher_skipped:    'Dispatcher chose Do Not Send',
  draft_never_notifies:  'Shift was still in draft',
  internal_edit_only:    'Internal-only edit — no customer-facing change',
  tech_skipped:          'Technician chose Skip',
  tech_skipped_review:   'Technician chose not to request review',
  user_opted_out:        'Customer opted out',
  no_phone:              'No phone number on file',
  invalid_phone:         'Phone number was invalid',
  globally_disabled:     'Global SMS was disabled',
  event_disabled:        'This SMS event was disabled in settings',
};

const fmtTime = (iso) => fmtETDateTime(iso);

export const CommunicationHistory = ({ history }) => {
  const [filter, setFilter] = useState('all'); // all | sent | skipped

  const counts = useMemo(() => ({
    all:     history.length,
    sent:    history.filter((r) => r.sent).length,
    skipped: history.filter((r) => !r.sent).length,
  }), [history]);

  const visible = useMemo(() => {
    if (filter === 'sent') return history.filter((r) => r.sent);
    if (filter === 'skipped') return history.filter((r) => !r.sent);
    return history;
  }, [history, filter]);

  if (!history.length) {
    return (
      <div data-testid="comm-history-empty" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4 text-[#A3A3A3] text-sm">
        No communications recorded for this customer yet.
      </div>
    );
  }

  return (
    <div data-testid="comm-history" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[#D4AF37] text-xs uppercase tracking-wider flex items-center gap-1.5">
          <MessageSquare className="w-3.5 h-3.5" /> Communication History
        </p>
        <div data-testid="comm-history-filters" className="flex items-center gap-1 bg-[#0A0A0A] border border-[#262626] rounded-md p-0.5">
          {[
            { k: 'all',     label: `All ${counts.all}` },
            { k: 'sent',    label: `Sent ${counts.sent}` },
            { k: 'skipped', label: `Skipped ${counts.skipped}` },
          ].map((f) => (
            <button
              key={f.k}
              type="button"
              data-testid={`comm-history-filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`px-2.5 py-1 text-[11px] rounded ${
                filter === f.k ? 'bg-[#D4AF37] text-[#0A0A0A] font-medium' : 'text-[#A3A3A3] hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {visible.length === 0 ? (
          <p className="text-[#525252] text-xs">No entries match this filter.</p>
        ) : visible.map((row) => {          const isSent = !!row.sent;
          const isFailed = !isSent && !row.skip_reason;  // attempted but failed (e.g. Twilio error)
          const color = isSent ? '#16a34a' : (isFailed ? '#dc2626' : '#525252');
          const StatusIcon = isSent ? CheckCircle2 : (isFailed ? XCircle : Slash);
          const label = EVENT_LABEL[row.event] || row.event || 'Message';
          const reason = row.skip_reason ? (SKIP_REASON_LABEL[row.skip_reason] || row.skip_reason) : null;
          return (
            <div
              key={row.id}
              data-testid={`comm-history-row-${row.id}`}
              data-status={isSent ? 'sent' : (isFailed ? 'failed' : 'skipped')}
              className="border-l-2 pl-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded-r-md rounded-l-none"
              style={{ borderLeftColor: color }}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <StatusIcon className="w-3.5 h-3.5" style={{ color }} />
                <span className="text-white text-sm font-medium">{label}</span>
                {isSent ? (
                  <Badge className="bg-green-500/15 text-green-400 border-green-700 text-[10px] uppercase">Sent</Badge>
                ) : isFailed ? (
                  <Badge className="bg-red-500/15 text-red-400 border-red-700 text-[10px] uppercase">Failed</Badge>
                ) : (
                  <Badge className="bg-[#262626] text-[#A3A3A3] border-[#3f3f3f] text-[10px] uppercase">Skipped</Badge>
                )}
                <span className="text-[#525252] text-[10px] ml-auto">{fmtTime(row.created_at)}</span>
              </div>
              {reason && (
                <p className="text-[#A3A3A3] text-xs mt-1 flex items-start gap-1">
                  <AlertTriangle className="w-3 h-3 mt-0.5 text-amber-400 flex-shrink-0" />
                  <span>{reason}</span>
                </p>
              )}
              {row.error && (
                <p className="text-red-400 text-xs mt-1">{row.error}</p>
              )}
              {row.message_preview && (
                <p className="text-white text-xs mt-1.5 leading-relaxed line-clamp-3">{row.message_preview}</p>
              )}
              {(row.actor_name || row.to) && (
                <p className="text-[#525252] text-[10px] mt-1">
                  {row.actor_name && <>by <span className="text-[#A3A3A3]">{row.actor_name}</span></>}
                  {row.actor_name && row.to && ' · '}
                  {row.to && <>to <span className="font-mono">{row.to}</span></>}
                </p>
              )}
            </div>
          );
        })}
      </div>
      <p data-testid="comm-history-tz-note" className="text-[#525252] text-[10px] text-right">
        All times shown in {ET_TIMEZONE.replace('_', ' ')} (Eastern Time)
      </p>
    </div>
  );
};

export default CommunicationHistory;
